import { ShortUrl } from '@air/api';
import {
  contentCollectionApplyAllSelector,
  contentCollectionCustomFieldsSelector,
  contentCollectionFormIdSelector,
} from '@air/feature-content-collection/redux';
import { Upload, uploadByIdSelector } from '@air/redux-uploader';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { addUploadingClipToBoardCollectionAction } from '~/store/boardCollection/actions';
import { metadataForPublicUploadSelector } from '~/store/publicUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export const useFetchClipForContentCollectionUpload = () => {
  const dispatch = useDispatch();
  const store = useAirStore();

  const fetchClipForContentCollectionUpload = useCallback(
    async (uploadId: Upload['id']) => {
      const formId = contentCollectionFormIdSelector(store.getState());
      const upload = uploadByIdSelector(store.getState(), uploadId);
      const applyAll = contentCollectionApplyAllSelector(store.getState());
      const customFields = contentCollectionCustomFieldsSelector(store.getState());

      if (!upload) {
        return;
      }

      const uploadMetadata = metadataForPublicUploadSelector(store.getState(), uploadId);
      const shouldApplyCustomFields = !!formId && applyAll && customFields.length;

      const { clip } = await ShortUrl.getBoardClip(uploadMetadata.shortBoardId, upload.clipId);

      if (shouldApplyCustomFields && uploadMetadata.sessionId) {
        await ShortUrl.batchUpdateFormAssets(uploadMetadata.shortBoardId, formId, {
          assetIds: [clip.assetId],
          formSessionId: uploadMetadata.sessionId,
          update: {
            customFields: customFields.map((customField) => {
              return {
                customFieldId: customField.id,
                valueId: customField.value?.id ?? undefined,
                valueIdsToAdd: customField.values?.map(({ id }) => id) ?? undefined,
                plainTextValue: customField.plainTextValue ?? undefined,
                dateValue: customField.dateValue ?? undefined,
              };
            }),
          },
        });
      }

      dispatch(
        addUploadingClipToBoardCollectionAction({
          clip: shouldApplyCustomFields
            ? {
                ...clip,
                customFields: customFields.map((customField) => ({
                  ...customField,
                  assetId: clip.assetId,
                })),
              }
            : clip,
          uploadId,
        }),
      );

      return clip;
    },
    [dispatch, store],
  );

  return { fetchClipForContentCollectionUpload };
};
